/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppRoute } from '@agentselly/immodossier-shared';
import { createFileRoute, useLinkProps } from '@tanstack/react-router';
import { Waitlist } from '@clerk/clerk-react';
import { useTheme } from '~/context/ThemeContext';
import { dark } from '@clerk/themes';
import { useTranslate } from '@tolgee/react';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { LegacyWaitlistBody } from '~/components/auth/LegacyWaitlistBody';

function V3WaitlistBody() {
  const { t } = useTranslate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const { href: signInUrl } = useLinkProps({
    to: AppRoute.$LANG_$COMPANYID_INDEX as any,
  });

  return (
    <div className="flex items-start justify-center py-8 px-4 min-h-full">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {t('auth_waitlist_title', 'Warteliste')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t(
              'auth_waitlist_subtitle',
              'ImmoDossier wird schrittweise freigeschaltet. Tragen Sie sich ein, wir informieren Sie.',
            )}
          </p>
        </div>

        <div
          className="rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 shadow-sm dark:shadow-none p-6 sm:p-8 space-y-6"
          data-od-id="waitlist"
          aria-label="Warteliste"
        >
          <Waitlist
            afterJoinWaitlistUrl="https://www.agentselly.ch/?utm_source=clerk_waitlist&utm_medium=clerk_waitlist&utm_campaign=redirect&utm_content=%24%7B1%7D"
            signInUrl={signInUrl}
            appearance={{
              baseTheme: isDark ? dark : undefined,
              variables: {
                colorBackground: isDark ? '#18181b' : '#ffffff',
                colorText: isDark ? '#f4f4f5' : '#09090b',
                colorInputBackground: isDark ? '#18181b' : '#ffffff',
                colorInputText: isDark ? '#f4f4f5' : '#09090b',
                colorPrimary: isDark ? '#ffffff' : '#09090b',
                colorTextSecondary: isDark ? '#a1a1aa' : '#71717a',
                borderRadius: '0.5rem',
              },
              elements: {
                rootBox: 'w-full',
                card: 'shadow-none border-0 p-0 bg-transparent w-full',
                formButtonPrimary:
                  'bg-gray-950 dark:bg-white text-white dark:text-gray-950 hover:bg-gray-800 dark:hover:bg-gray-100 font-semibold min-h-[44px]',
                formFieldInput:
                  'border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-900 dark:text-gray-100 min-h-[44px]',
                header: 'hidden',
                footer: 'hidden',
              },
            }}
          />

          <div className="pt-2 flex items-center justify-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
            >
              <rect
                width="18"
                height="11"
                x="3"
                y="11"
                rx="2"
                ry="2"
              />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>
              {t('auth_waitlist_managed_by_clerk', 'Verwaltet von Clerk')}
            </span>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <span className="mr-1.5">
            {t('auth_waitlist_already_unlocked', 'Schon freigeschaltet?')}
          </span>
          <a
            href={signInUrl}
            className="font-semibold text-gray-900 dark:text-gray-100 underline hover:text-primary-500 transition-colors"
          >
            {t('auth_signin_title', 'Anmelden')}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_WAITLIST as any,
)({
  component: function Entry() {
    return useV3Redesign() ? <V3WaitlistBody /> : <LegacyWaitlistBody />;
  },
});
